import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  IconButton,
  LinearProgress,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { InfoOutlined as InfoIcon, Share as ShareIcon } from '@mui/icons-material';
import { BRAND_COLORS, alpha } from '../../styles/theme';
import { useTranslation } from '../../i18n';
import type { TranslationKey } from '../../i18n/useTranslation';
import { useErrorHandler } from '../../hooks/useErrorHandler';
import { getErrorMessage } from '../../utils/errorHandler';
import { pendingTargetId } from '../../utils/pendingTargetId';
import { useAuthV2 } from '../../hooks/useAuthV2';
import {
  useFlightSharesQuery,
  useShareCounterpartiesQuery,
} from '../flight-shares/flightShares.queries';
import { useRevokeFlightShareMutation } from '../flight-shares/flightShares.mutations';
import { isForbiddenError } from '../flight-shares/shareErrors';
import ShareDroneFlightsDialog from '../flight-shares/ShareDroneFlightsDialog';
import { useSharedObjectsQuery } from '../object-shares/sharedObjects.queries';
import { useSharedObjectDownloadMutation } from '../object-shares/sharedObjects.mutations';
import { useRevokeObjectShareMutation } from '../object-shares/objectShares.mutations';
import { openAnalyticsWithFiles } from '../file-browser/utils';
import { pruneSelection } from '../file-browser/selectionModel';
import { useFileSelection } from '../file-browser/hooks/useFileSelection';
import { useBrowserShortcuts } from '../file-browser/hooks/useBrowserShortcuts';
import { useBulkDownload } from '../file-browser/hooks/useBulkDownload';
import BulkDownloadStatusLine from '../file-browser/BulkDownloadStatusLine';
import SharedEntryMenu from './SharedEntryMenu';
import SharedSectionPanel from './SharedSectionPanel';
import {
  INITIAL_SECTION_STATE,
  SHARED_KINDS,
  SHARED_PAGE_SIZE,
  entryLabel,
  flightEntries,
  mergeCounterpartyOptions,
  nextDirectionKnown,
  objectEntries,
  pageOffset,
  renderedEntries,
  resetToFirstPage,
  sectionShares,
  sharedMenuModel,
  sharedSections,
  withCounterparty,
  withIncludeRevoked,
  withLoadedPage,
  withPage,
  type CounterpartyOption,
  type SectionListState,
  type SharedEntry,
  type SharedKind,
  type SharedMenuModel,
} from './sharedViewModel';

interface SharedViewProps {
  onBack: () => void;
  /**
   * Browse into a shared FOLDER (STD-029 B-4, closing D-6).
   *
   * The hand-off, not a second browser. A folder share is one row covering a
   * subtree the share table cannot enumerate, so this screen can list the grant
   * and never its contents; the Files browser lists contents, and since B-4 it
   * addresses a folder by id, which is the only spelling a share can authorize.
   * Rendering an inline browser here instead would be a second implementation of
   * the grid, the selection, the context menu and the drag targets — all of which
   * would drift.
   *
   * Undefined leaves the menu item hidden, which is what a caller that has no
   * Files browser to hand off to should do.
   */
  onOpenFolder?: (objectId: string) => void;
  /**
   * Whether this caller may GRANT and REVOKE, i.e. is an org admin.
   *
   * Passed in rather than inferred here, and deliberately not inferred from a
   * failed request: "the counterparties call 403'd" is evidence about one
   * endpoint at one moment, and a network blip wearing the same shape would
   * silently strip an admin's controls. Viewing is a separate capability — any
   * authenticated user reaches this screen, because neither listing is
   * role-gated and RLS already scopes the rows (#1206).
   */
  canAdministerShares: boolean;
}

const KIND_LABEL_KEYS: Record<SharedKind, TranslationKey> = {
  flights: 'sharedView.kindFlights',
  files: 'sharedView.kindFiles',
};

/**
 * "Shared with me" — one surface over everything that crosses this
 * organization's boundary (#1242, #1247).
 *
 * ### Why one screen and not two
 *
 * There used to be two adjacent nav entries, "Shared" (flights) and "Shared
 * files" (objects), partitioned by which grant table was written — a split a
 * user has no way to see. The same CSV was a legitimate subject for either kind
 * of grant, so which door it appeared behind depended on which dialog the
 * granter opened, and nothing answered "what has been shared with me?".
 *
 * ### The two axes, and which one is a request
 *
 * DIRECTION is a request: each section asks the server for exactly one, because
 * neither endpoint has a "both" mode and a union would page rows that do not
 * mean the same thing. KIND is a toggle across the whole screen, and it selects
 * which endpoint each section calls. So the Flights/Files toggle lives *within*
 * a direction, never across one.
 *
 * Both directions stay on screen at once. A sharing screen that showed only what
 * you gave away, or only what you received, would answer half of the only
 * question it exists for.
 *
 * ### Not a folder
 *
 * A peer view of the Files browser, reached from the breadcrumb bar and
 * replacing it — never a pseudo-directory inside it. Grafting another org's
 * subtree into this org's path namespace is exactly the ambiguity the object
 * registry exists to remove, and it is why the backend refuses to fold either
 * listing into `/api/files/browse`.
 */
export const SharedView: React.FC<SharedViewProps> = ({
  onBack,
  onOpenFolder,
  canAdministerShares,
}) => {
  const { t } = useTranslation();
  const { showSuccess } = useErrorHandler();
  const { profile } = useAuthV2();

  const [kind, setKind] = useState<SharedKind>('flights');
  const [bulkOpen, setBulkOpen] = useState(false);
  const [pageSize, setPageSize] = useState(SHARED_PAGE_SIZE);

  const [primaryState, setPrimaryState] = useState<SectionListState>(INITIAL_SECTION_STATE);
  const [inboundState, setInboundState] = useState<SectionListState>(INITIAL_SECTION_STATE);

  const [primaryOptions, setPrimaryOptions] = useState<CounterpartyOption[]>([]);
  const [inboundOptions, setInboundOptions] = useState<CounterpartyOption[]>([]);

  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [menuEntry, setMenuEntry] = useState<SharedEntry | null>(null);
  const [menuModel, setMenuModel] = useState<SharedMenuModel | null>(null);

  // Fetched only for an admin, and only for the bulk-share dialog's grantee
  // picker — `GET /flight-shares/organizations` is `require_org_admin`. Not
  // requesting it for a member is what keeps a 403 off their screen: they have
  // done nothing wrong, and an error banner on every visit would say otherwise.
  // The listings themselves stopped depending on this call when direction and
  // counterparty names moved into the payloads.
  const counterpartiesQuery = useShareCounterpartiesQuery(canAdministerShares);
  const counterparties = counterpartiesQuery.data;
  const ownOrgId = counterparties?.own_organization_id;

  // One mutation instance per action for the whole grid; which ROW each is
  // acting on is `pendingTargetId`'s job. Download is shared across both kinds
  // deliberately — `GET /api/file-objects/{object_id}/download` is the single
  // endpoint that serves a shared flight's log and a shared file alike, because
  // it authorizes by asking whether the caller may SELECT the row.
  const download = useSharedObjectDownloadMutation();
  // A second instance for "Download (N files)", so a file that fails inside a
  // bulk run is reported in the run's own outcome line and does not also raise
  // the single-download alert below for a row nobody clicked.
  const bulkPresign = useSharedObjectDownloadMutation();
  const revokeFlightShare = useRevokeFlightShareMutation();
  const revokeObjectShare = useRevokeObjectShareMutation();

  const isFlights = kind === 'flights';

  // Direction is never assumed for FLIGHTS. The server states it per share;
  // failing that the caller's own org id derives it; failing both, the listing
  // refuses to split and renders undivided with no Revoke control — we will not
  // claim a direction we cannot derive, nor offer a control only the granting
  // side can use. `SharedObject.direction` is required on the wire, so the files
  // listing never reaches this.
  const [directionKnown, setDirectionKnown] = useState(true);

  // `includeRevoked` is per SECTION (#1498): it is a filter on one listing and
  // sits beside that listing's counterparty filter, so each direction asks the
  // server its own question.
  const flightsPrimaryQuery = useFlightSharesQuery(
    {
      includeRevoked: primaryState.includeRevoked,
      direction: directionKnown ? 'outbound' : undefined,
      counterpartyOrganizationId: primaryState.counterpartyId || undefined,
      limit: pageSize,
      offset: pageOffset(primaryState.page, pageSize),
    },
    isFlights
  );

  const flightsInboundQuery = useFlightSharesQuery(
    {
      includeRevoked: inboundState.includeRevoked,
      direction: 'inbound',
      counterpartyOrganizationId: inboundState.counterpartyId || undefined,
      limit: pageSize,
      offset: pageOffset(inboundState.page, pageSize),
    },
    isFlights && directionKnown
  );

  const filesPrimaryQuery = useSharedObjectsQuery(
    {
      direction: 'outbound',
      counterpartyOrganizationId: primaryState.counterpartyId || undefined,
      limit: pageSize,
      offset: pageOffset(primaryState.page, pageSize),
    },
    !isFlights
  );

  const filesInboundQuery = useSharedObjectsQuery(
    {
      direction: 'inbound',
      counterpartyOrganizationId: inboundState.counterpartyId || undefined,
      limit: pageSize,
      offset: pageOffset(inboundState.page, pageSize),
    },
    !isFlights
  );

  const flightShares = useMemo(
    () => flightsPrimaryQuery.data?.shares ?? [],
    [flightsPrimaryQuery.data]
  );

  useEffect(() => {
    if (!isFlights) return;
    setDirectionKnown((current) => nextDirectionKnown(current, flightShares, ownOrgId));
  }, [isFlights, flightShares, ownOrgId]);

  const sections = useMemo(
    () => sharedSections(kind, directionKnown, canAdministerShares),
    [kind, directionKnown, canAdministerShares]
  );

  const primarySection = sections[0];

  const primaryEntries = useMemo(() => {
    if (!isFlights) return objectEntries(filesPrimaryQuery.data?.objects ?? []);
    return flightEntries(sectionShares(flightShares, primarySection.direction, ownOrgId));
  }, [isFlights, filesPrimaryQuery.data, flightShares, primarySection.direction, ownOrgId]);

  const inboundEntries = useMemo(() => {
    if (!isFlights) return objectEntries(filesInboundQuery.data?.objects ?? []);
    return flightEntries(
      sectionShares(flightsInboundQuery.data?.shares ?? [], 'inbound', ownOrgId)
    );
  }, [isFlights, filesInboundQuery.data, flightsInboundQuery.data, ownOrgId]);

  // What the SERVER returned, kept apart from `entries` — an empty screen may
  // only claim "nothing is shared" against this, never against a list the
  // client's own direction filter emptied.
  const primaryPage = useMemo(() => {
    const data = isFlights ? flightsPrimaryQuery.data : filesPrimaryQuery.data;
    if (!data) return undefined;
    const rows = 'shares' in data ? data.shares : data.objects;
    return { rowCount: rows.length, total: data.total };
  }, [isFlights, flightsPrimaryQuery.data, filesPrimaryQuery.data]);

  const inboundPage = useMemo(() => {
    const data = isFlights ? flightsInboundQuery.data : filesInboundQuery.data;
    if (!data) return undefined;
    const rows = 'shares' in data ? data.shares : data.objects;
    return { rowCount: rows.length, total: data.total };
  }, [isFlights, flightsInboundQuery.data, filesInboundQuery.data]);

  const primaryQuery = isFlights ? flightsPrimaryQuery : filesPrimaryQuery;
  const inboundQuery = isFlights ? flightsInboundQuery : filesInboundQuery;

  // Dropdown options accumulate across pages and survive filtering — see
  // `mergeCounterpartyOptions`. Merging returns the previous array unchanged
  // when it learns nothing, so these effects settle immediately.
  useEffect(() => {
    setPrimaryOptions((prev) => mergeCounterpartyOptions(prev, primaryEntries));
  }, [primaryEntries]);

  useEffect(() => {
    setInboundOptions((prev) => mergeCounterpartyOptions(prev, inboundEntries));
  }, [inboundEntries]);

  // Remembering each set's size, and keeping the page inside it. Both rules live
  // in `withLoadedPage` — absent data changes nothing, an arrived page is
  // remembered and clamps the current one — so they are unit-tested rather than
  // argued about here. Applying it returns the same object when nothing moved,
  // so these effects settle in one pass.
  useEffect(() => {
    setPrimaryState((state) => withLoadedPage(state, primaryPage, pageSize));
  }, [primaryPage, pageSize]);

  useEffect(() => {
    setInboundState((state) => withLoadedPage(state, inboundPage, pageSize));
  }, [inboundPage, pageSize]);

  // Every control that re-scopes a result set sends both sections back to their
  // first page — page 3 of the old set indexes nothing in the new one, and the
  // empty page it would fetch is one the view is entitled to describe as
  // "nothing is shared".
  const resetPaging = () => {
    setPrimaryState(resetToFirstPage);
    setInboundState(resetToFirstPage);
  };

  const handleKindChange = (next: SharedKind | null) => {
    if (!next || next === kind) return;
    setKind(next);
    resetPaging();
    // Options are "counterparties seen in THIS listing". Carrying flight
    // counterparties into the files tab would offer a filter that can only
    // return an empty page, which reads as "they shared nothing" when the truth
    // is "they shared nothing OF THIS KIND".
    setPrimaryOptions([]);
    setInboundOptions([]);
  };

  // ---- Selection (#1692): the Files browser's hooks, keyed on `SharedEntry.key`.
  //
  // ONE selection across both sections, in the order the grids render, so
  // Shift-click and Ctrl+A walk what the user sees. What stops being on screen
  // (a page turn, a filter, the Flights/Files toggle) leaves the selection.
  const selection = useFileSelection();
  const visibleEntries = useMemo(
    () =>
      sections.flatMap((section) =>
        renderedEntries(section.key === 'inbound' ? inboundEntries : primaryEntries)
      ),
    [sections, inboundEntries, primaryEntries]
  );
  const orderedKeys = useMemo(() => visibleEntries.map((entry) => entry.key), [visibleEntries]);

  const { selectedIds, setSelected } = selection;
  useEffect(() => {
    const kept = pruneSelection(selectedIds, orderedKeys);
    if (kept !== selectedIds) setSelected(kept);
  }, [selectedIds, orderedKeys, setSelected]);

  // Select-all and clear. There is nothing to cut or paste into on this
  // screen, so Ctrl+X/V are left to the browser.
  useBrowserShortcuts({
    selectAll: () => selection.selectAllIds(orderedKeys),
    clear: () => selection.clear(),
  });

  const handleItemClick = (entry: SharedEntry, event: React.MouseEvent<HTMLElement>) => {
    selection.selectOnClick(
      entry.key,
      { ctrlOrMeta: event.ctrlKey || event.metaKey, shift: event.shiftKey },
      orderedKeys
    );
  };

  // "Download (N files)" — the same run as the Files browser's.
  const presignBulk = useCallback(
    async (entry: SharedEntry) =>
      entry.objectId ? (await bulkPresign.mutateAsync(entry.objectId)).download_url : undefined,
    [bulkPresign]
  );
  const nameOfEntry = useCallback((entry: SharedEntry) => entryLabel(entry, t), [t]);
  const bulkDownload = useBulkDownload(presignBulk, nameOfEntry);

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    resetPaging();
  };

  const closeMenu = () => {
    setMenuAnchor(null);
    setMenuEntry(null);
    setMenuModel(null);
  };

  /**
   * Open a shared folder in the Files browser (D-6).
   *
   * Guarded on `folderId` rather than on `isFolder`: a revoked share nulls it,
   * and navigating there would 404 — the listing resolves the row under the
   * caller's own RLS, and a revoke closes that in the same statement.
   */
  const handleOpenFolder = (entry: SharedEntry) => {
    closeMenu();
    if (!entry.folderId) return;
    onOpenFolder?.(entry.folderId);
  };

  // From the menu: one file the usual way, several through the bulk run.
  const handleDownloadTargets = (targets: SharedEntry[]) => {
    closeMenu();
    if (targets.length > 1) {
      bulkDownload.start(targets);
    } else if (targets.length === 1) {
      void handleDownload(targets[0]);
    }
  };

  const handleDownload = async (entry: SharedEntry) => {
    closeMenu();
    if (!entry.objectId) return;
    try {
      const result = await download.mutateAsync(entry.objectId);
      if (result.download_url) {
        // `noopener,noreferrer`: the presigned URL points at another
        // organization's object, so the opened tab is the one place here that
        // handles content this org does not own. Denying it `window.opener`
        // costs nothing and closes reverse-tabnabbing.
        window.open(result.download_url, '_blank', 'noopener,noreferrer');
        showSuccess(t('sharedView.downloadStarted'));
      }
    } catch {
      // Rendered inline by the alert below — a 404 here means access ended
      // between the listing and the click, which deserves to stay on screen.
    }
  };

  const handlePlot = (entry: SharedEntry) => {
    closeMenu();
    if (!entry.objectId || !entry.name) return;
    // No `s3_key`, on purpose. A shared object has no path in THIS
    // organization's namespace, and analytics-backend resolves paths against the
    // caller's own org — so a path handed over here would 404, or worse, quietly
    // open a same-named file of the caller's own. See `analyticsHandoffPayload`.
    openAnalyticsWithFiles(
      [{ filename: entry.name, object_id: entry.objectId }],
      profile?.organization?.slug ?? null
    );
  };

  const handleRevoke = async (entry: SharedEntry) => {
    closeMenu();
    const orgLabel = entry.counterpartyName ?? t('sharedView.unknownOrg');
    if (!window.confirm(t('sharedView.revokeConfirm', { org: orgLabel }))) return;
    try {
      if (entry.kind === 'flights') {
        await revokeFlightShare.mutateAsync(entry.shareId);
      } else {
        await revokeObjectShare.mutateAsync(entry.shareId);
      }
      showSuccess(t('sharedView.revokeSuccess'));
    } catch {
      // Rendered inline by the alert below.
    }
  };

  // Scoped to the row that actually asked, never the whole grid — the rule and
  // why it is ordered the way it is live in `pendingTargetId`, so they are
  // unit-tested rather than restated here. #1232 is the revoke half of this, and
  // it is the sharper one: an admin who cannot withdraw the second grant because
  // the first succeeded is an operational problem.
  const downloadingObjectId = pendingTargetId(download.isPending, download.variables);
  const revokingShareId =
    pendingTargetId(revokeFlightShare.isPending, revokeFlightShare.variables) ??
    pendingTargetId(revokeObjectShare.isPending, revokeObjectShare.variables);

  const revokeError = revokeFlightShare.error ?? revokeObjectShare.error;

  return (
    <Box data-testid="shared-view">
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button onClick={onBack} variant="outlined" size="small">
            {t('sharedView.backToFiles')}
          </Button>
          <Typography
            variant="h5"
            sx={{ color: BRAND_COLORS.forestGreen.main, fontWeight: 'bold' }}
          >
            {t('sharedView.title')}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {/* Flights or Files — one toggle for the whole screen, applied WITHIN
              each direction below. */}
          <ToggleButtonGroup
            size="small"
            exclusive
            value={kind}
            onChange={(_e, next) => handleKindChange(next as SharedKind | null)}
            data-testid="shared-kind-toggle"
          >
            {SHARED_KINDS.map((option) => (
              <ToggleButton key={option} value={option} data-testid={`shared-kind-${option}`}>
                {t(KIND_LABEL_KEYS[option])}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
          {/* Granting is `require_org_admin`; a member is not offered a button
              whose dialog would 403 on open and again on submit. */}
          {canAdministerShares && isFlights && (
            <Button
              variant="contained"
              startIcon={<ShareIcon />}
              onClick={() => setBulkOpen(true)}
              data-testid="open-bulk-share-btn"
              sx={{
                backgroundColor: BRAND_COLORS.forestGreen.main,
                '&:hover': { backgroundColor: alpha(BRAND_COLORS.forestGreen.main, 0.8) },
              }}
            >
              {t('sharedView.openBulk')}
            </Button>
          )}
        </Box>
      </Box>

      {/* One line of orientation; the mechanics live behind the info icon
          rather than as a paragraph of doctrine above the listing (#1498). */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 2 }}>
        <Typography variant="body2" color="text.secondary">
          {t(isFlights ? 'sharedView.flightsIntro' : 'sharedView.filesIntro')}
        </Typography>
        <Tooltip
          title={t(isFlights ? 'sharedView.flightsIntroDetail' : 'sharedView.filesIntroDetail')}
        >
          <IconButton
            size="small"
            aria-label={t('sharedView.aboutThisPage')}
            data-testid="shared-view-intro-info"
          >
            <InfoIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Says why the controls are absent, rather than leaving a viewer to
          wonder whether the screen is broken. A caption, not an Alert: nothing
          has gone wrong, and a member reaching a read-only screen they are
          entitled to read is not an error condition. */}
      {!canAdministerShares && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'block', mb: 2 }}
          data-testid="shared-view-read-only-note"
        >
          {t('sharedView.onlyAdmins')}
        </Typography>
      )}

      {(primaryQuery.isFetching || inboundQuery.isFetching || counterpartiesQuery.isFetching) && (
        <LinearProgress sx={{ mb: 2 }} />
      )}

      {primaryQuery.isError && (
        <Alert severity="error" sx={{ mb: 2 }} data-testid="shared-view-primary-error">
          {getErrorMessage(primaryQuery.error)}
        </Alert>
      )}

      {inboundQuery.isError && (
        <Alert severity="error" sx={{ mb: 2 }} data-testid="shared-view-inbound-error">
          {getErrorMessage(inboundQuery.error)}
        </Alert>
      )}

      {download.isError && (
        <Alert severity="error" sx={{ mb: 2 }} data-testid="shared-view-download-error">
          {getErrorMessage(download.error)}
        </Alert>
      )}

      <BulkDownloadStatusLine status={bulkDownload.status} onDismiss={bulkDownload.dismiss} />

      {revokeError && (
        <Alert severity="error" sx={{ mb: 2 }} data-testid="shared-view-revoke-error">
          {getErrorMessage(revokeError)}
        </Alert>
      )}

      {/* Only an admin can reach this at all — the query is disabled otherwise,
          so a member never issues the request that would 403 and never sees a
          banner for it. For an admin the call is the bulk-share picker, and a
          403 here means the server disagrees that they may grant; anything else
          — a 500, a dropped connection, the endpoint's own
          SHARE_OWN_ORG_NOT_FOUND — must report itself, or a real admin is told
          they lack permissions they hold. Either way the listings below are
          unaffected: they stopped depending on this call. */}
      {canAdministerShares &&
        counterpartiesQuery.isError &&
        (isForbiddenError(counterpartiesQuery.error) ? (
          <Alert severity="info" sx={{ mb: 2 }} data-testid="shared-view-no-access">
            {t('sharedView.onlyAdmins')}
          </Alert>
        ) : (
          <Alert severity="error" sx={{ mb: 2 }} data-testid="shared-view-counterparties-error">
            {getErrorMessage(counterpartiesQuery.error)}
          </Alert>
        ))}

      {sections.map((section) => {
        const isInbound = section.key === 'inbound';
        const query = isInbound ? inboundQuery : primaryQuery;
        const state = isInbound ? inboundState : primaryState;
        const setState = isInbound ? setInboundState : setPrimaryState;
        const page = isInbound ? inboundPage : primaryPage;
        return (
          <SharedSectionPanel
            key={section.key}
            section={section}
            kind={kind}
            entries={isInbound ? inboundEntries : primaryEntries}
            rowCount={page?.rowCount ?? 0}
            total={state.total}
            fetchSucceeded={query.isSuccess}
            counterparties={counterparties}
            options={isInbound ? inboundOptions : primaryOptions}
            selectedCounterpartyId={state.counterpartyId}
            onCounterpartyChange={(id) => setState((s) => withCounterparty(s, id))}
            // Flights only: reach has no revoked state — a revoked object share
            // simply stops reaching, so there is nothing for the switch to add.
            includeRevoked={isFlights ? state.includeRevoked : undefined}
            onIncludeRevokedChange={
              isFlights ? (value) => setState((s) => withIncludeRevoked(s, value)) : undefined
            }
            page={state.page}
            pageSize={pageSize}
            onPageChange={(next) => setState((s) => withPage(s, next))}
            onPageSizeChange={handlePageSizeChange}
            selectedKeys={selection.selectedIds}
            onItemClick={handleItemClick}
            onBackgroundClick={selection.clear}
            onMarqueeSelect={(keys) => selection.setSelected(new Set(keys))}
            // Double-click / Enter on a card. A FOLDER opens; a file
            // downloads. One callback because the grid has one open gesture,
            // and the row decides what opening it means — the same rule the
            // Files browser's `handleItemOpen` follows.
            onOpen={(entry) => (entry.isFolder ? handleOpenFolder(entry) : handleDownload(entry))}
            onContextMenu={(event, entry) => {
              event.preventDefault();
              event.stopPropagation();
              // Explorer's right-click: an entry outside the selection replaces
              // it; one inside it acts on all of it. Resolved here rather than
              // after the state update, which has not happened yet.
              const inSelection = selection.isSelected(entry.key);
              const effective = inSelection ? selection.selectedIds : new Set([entry.key]);
              if (!inSelection) selection.replaceWith(entry.key);
              const model = sharedMenuModel(entry, section, effective, visibleEntries);
              // Nothing to offer, no menu — not an empty one (#1692).
              if (model.isEmpty) return;
              setMenuEntry(entry);
              setMenuModel(model);
              setMenuAnchor(event.currentTarget);
            }}
          />
        );
      })}

      <SharedEntryMenu
        anchorEl={menuAnchor}
        entry={menuEntry}
        model={menuModel}
        downloadPending={!!menuEntry && downloadingObjectId === menuEntry.objectId}
        revokePending={!!menuEntry && revokingShareId === menuEntry.shareId}
        onClose={closeMenu}
        onOpen={handleOpenFolder}
        onDownload={handleDownloadTargets}
        onPlot={handlePlot}
        onRevoke={handleRevoke}
      />

      {canAdministerShares && (
        <ShareDroneFlightsDialog open={bulkOpen} onClose={() => setBulkOpen(false)} />
      )}
    </Box>
  );
};

export default SharedView;
